"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import {
  Badge as LucideBadge,
  Eye,
  Heart,
  ShoppingBag,
  Truck,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { Badge, Button } from "@eg/ui";
import ImageWithFallback from "../ImageWithFallback";
import { useCartStore } from "@/store/cart";
import { formatPrice } from "@/lib/format";
import type { EnrichedProduct } from "@/lib/discovery";
import { useLikesStore } from "@/store/likes";
import { useAuthStore } from "@/store/auth";
import { useLocale, useTranslations } from "@/lib/i18n/I18nProvider";

type Props = {
  product: EnrichedProduct;
  highlight?: boolean;
  onQuickView: (product: EnrichedProduct) => void;
};

export function ProductCard({ product, highlight, onQuickView }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const add = useCartStore((s) => s.addItem);
  const liked = useLikesStore((s) => s.likedIds.has(product.id));
  const counts = useLikesStore((s) => s.counts[product.id]);
  const setCount = useLikesStore((s) => s.setCount);
  const toggleLike = useLikesStore((s) => s.toggleLike);
  const user = useAuthStore((s) => s.user);
  const [likeError, setLikeError] = useState<string | null>(null);

  useEffect(() => {
    setCount(product.id, product.likesCount ?? 0);
  }, [product.id, product.likesCount, setCount]);

  const likesCount = counts ?? product.likesCount ?? 0;

  const firstAvailableVariant = useMemo(() => {
    return (
      product.variants.find((v) => v.stock > 0) ?? product.variants[0] ?? null
    );
  }, [product.variants]);

  const price = useMemo(() => {
    if (product.minPriceCents === null) return "–";
    return formatPrice(
      product.minPriceCents,
      product.currency ?? "EUR",
      locale,
    );
  }, [locale, product.currency, product.minPriceCents]);

  const compare = useMemo(() => {
    if (!product.compareAtMinCents) return null;
    return formatPrice(
      product.compareAtMinCents,
      product.currency ?? "EUR",
      locale,
    );
  }, [locale, product.compareAtMinCents, product.currency]);

  const badgeLabels = useMemo<Record<string, string>>(
    () => ({
      sale: t("discovery.product.badges.sale"),
      new: t("discovery.product.badges.new"),
      shipping: t("discovery.product.badges.shipping"),
    }),
    [t],
  );

  const categoryLabels = useMemo(
    () => ({
      vaporizer: t("categories.vaporizer"),
      grinder: t("categories.grinder"),
      papers: t("categories.papers"),
      filterTips: t("categories.filterTips"),
      cones: t("categories.cones"),
      lighters: t("categories.lighters"),
      storage: t("categories.storage"),
      accessories: t("categories.accessories"),
      cleaning: t("categories.cleaning"),
      scales: t("categories.scales"),
      humidor: t("categories.humidor"),
      assortment: t("categories.assortment"),
    }),
    [t],
  );

  const handleAddToCart = () => {
    if (!firstAvailableVariant) return;
    add(
      {
        variantId: firstAvailableVariant.id,
        variantSlug: firstAvailableVariant.slug,
        productTitle: product.title,
        variantTitle: firstAvailableVariant.title,
        priceCents: firstAvailableVariant.priceCents,
        currency: firstAvailableVariant.currency,
        image: product.heroImage,
      },
      1,
    );
  };

  const handleToggleLike = async () => {
    if (!user) {
      window.location.href = `/login?callbackUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      return;
    }
    try {
      setLikeError(null);
      await toggleLike(product.id);
    } catch (err) {
      console.error(err);
      setLikeError(t("discovery.product.likeSaveError"));
    }
  };

  const soldOut = !firstAvailableVariant || firstAvailableVariant.stock <= 0;

  return (
    <article
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-within:-translate-y-0.5 focus-within:shadow-lg ${
        highlight ? "border-cyan-500 ring-2 ring-cyan-200" : "border-slate-200"
      }`}
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        <Link
          href={`/produkte/${product.slug}`}
          className="block h-full w-full"
        >
          <div className="absolute left-3 top-3 z-10 flex flex-wrap gap-2">
            {product.badges.map((badge) => (
              <Badge
                key={badge}
                variant={badge === "sale" ? "default" : "outline"}
                className="border-slate-300 bg-white/95 text-xs text-slate-800 shadow-sm backdrop-blur"
              >
                {badgeLabels[badge] ?? badge}
              </Badge>
            ))}
          </div>
          <div className="relative h-full w-full bg-gradient-to-b from-brand-50 to-gray-50">
            <ImageWithFallback
              src={product.heroImage}
              alt={product.title}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
            />
            {product.hoverImage ? (
              <ImageWithFallback
                src={product.hoverImage}
                alt={t("common.alternateImage", { title: product.title })}
                className="absolute inset-0 h-full w-full object-cover opacity-0 transition duration-500 group-hover:opacity-100"
              />
            ) : null}
          </div>
        </Link>

        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent opacity-0 transition group-hover:opacity-100" />

        <div className="absolute inset-x-3 bottom-3 hidden flex-col gap-2 opacity-0 transition group-hover:opacity-100 md:flex">
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label={
                liked
                  ? t("discovery.product.unlike")
                  : t("discovery.product.like")
              }
              className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border bg-white/90 text-sm font-medium text-gray-700 shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 ${
                liked ? "border-brand-400 text-brand-600" : "border-gray-200"
              }`}
              onClick={handleToggleLike}
            >
              <Heart
                className={`h-4 w-4 ${
                  liked ? "fill-brand-500 text-brand-600" : "text-gray-600"
                }`}
              />
            </button>
            <Button
              variant="secondary"
              size="sm"
              className="flex-1 border border-slate-300 bg-white text-xs font-semibold text-slate-800 shadow-sm hover:bg-slate-100 sm:text-sm"
              onClick={() => onQuickView(product)}
            >
              <Eye className="h-4 w-4" />
              {t("discovery.product.quickView")}
            </Button>
          </div>
          <Button
            variant="accent"
            size="sm"
            className="w-full shadow-md text-xs font-semibold sm:text-sm"
            disabled={soldOut}
            onClick={handleAddToCart}
          >
            <ShoppingBag className="h-4 w-4" />
            {t("discovery.product.addToCart")}
          </Button>
        </div>
      </div>

      <div className="flex flex-1 flex-col space-y-2 p-4">
        <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wide text-gray-500">
          <span>{product.brand}</span>
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500">
            <LucideBadge className="h-3 w-3" />
            {categoryLabels[product.category] ?? product.category}
          </span>
        </div>
        <h3 className="line-clamp-3 font-semibold text-gray-900">
          <Link
            href={`/produkte/${product.slug}`}
            className="focus:outline-none"
          >
            {product.title}
          </Link>
        </h3>
        <p className="line-clamp-2 text-sm text-gray-600">
          {product.description ?? t("discovery.product.fallbackDescription")}
        </p>

        <div className="flex items-center gap-2 md:hidden">
          <button
            type="button"
            aria-label={
              liked
                ? t("discovery.product.unlike")
                : t("discovery.product.like")
            }
            className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border bg-white text-gray-700 shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 ${
              liked ? "border-brand-400 text-brand-600" : "border-gray-200"
            }`}
            onClick={handleToggleLike}
          >
            <Heart
              className={`h-4 w-4 ${
                liked ? "fill-brand-500 text-brand-600" : "text-gray-600"
              }`}
            />
          </button>
          <Button
            variant="secondary"
            size="sm"
            className="flex-1 border border-slate-300 bg-white text-xs font-semibold text-slate-800 shadow-sm hover:bg-slate-100"
            onClick={() => onQuickView(product)}
          >
            <Eye className="h-4 w-4" />
            {t("discovery.product.quickView")}
          </Button>
          <Button
            variant="accent"
            size="sm"
            className="flex-1 text-xs font-semibold"
            disabled={soldOut}
            onClick={handleAddToCart}
          >
            <ShoppingBag className="h-4 w-4" />
            {t("discovery.product.addToCart")}
          </Button>
        </div>

        <div className="flex items-baseline gap-2">
          <span className="text-lg font-semibold text-gray-900">{price}</span>
          {compare ? (
            <span className="text-sm text-gray-400 line-through">
              {compare}
            </span>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs font-medium">
          {soldOut ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-rose-700">
              <AlertCircle className="h-4 w-4" />
              {t("discovery.product.notAvailable")}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-emerald-700">
              <CheckCircle className="h-4 w-4" />
              {t("discovery.product.inStock")}
            </span>
          )}

          <span className="hidden items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-slate-600 sm:inline-flex">
            <Truck className="h-4 w-4" />
            {t("discovery.product.readyToShip")}
          </span>
          <span className="hidden items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-slate-600 sm:inline-flex">
            <Heart className="h-4 w-4 text-rose-500" />
            {likesCount}
          </span>
        </div>
        {likeError ? <p className="text-xs text-error">{likeError}</p> : null}
      </div>
    </article>
  );
}
